const ASCII_BULLET_MARKER_CLASS = '[-*+]';
const SYMBOL_BULLET_MARKER_CLASS = '[•‣∙●◦▪▫◆◇·]';
const BULLET_MARKER_CLASS = `(?:${ASCII_BULLET_MARKER_CLASS}|${SYMBOL_BULLET_MARKER_CLASS})`;
const BULLET_LINE_REGEX = new RegExp(`^\\s*(?:${BULLET_MARKER_CLASS})\\s+\\S`);
const INLINE_BULLET_COUNT_REGEX = new RegExp(
  `\\s+${ASCII_BULLET_MARKER_CLASS}\\s*\\S`,
  'g'
);
const INLINE_BULLET_SPLIT_REGEX = new RegExp(
  `\\s+${ASCII_BULLET_MARKER_CLASS}\\s*`,
  'g'
);
const INLINE_BULLET_PUNCTUATION_REGEX = new RegExp(
  `[.!?;:]\\s*${ASCII_BULLET_MARKER_CLASS}\\s*\\S`
);
const INLINE_SYMBOL_BULLET_COUNT_REGEX = new RegExp(
  `${SYMBOL_BULLET_MARKER_CLASS}\\s*\\S`,
  'g'
);
const INLINE_SYMBOL_BULLET_SPLIT_REGEX = new RegExp(
  `\\s*${SYMBOL_BULLET_MARKER_CLASS}\\s*`,
  'g'
);
const BULLET_NORMALIZE_REGEX = new RegExp(
  `^(\\s*)(${BULLET_MARKER_CLASS})\\s*(\\S)`
);

const isBulletLine = (line: string | null | undefined): boolean => {
  if (!line) {
    return false;
  }
  return BULLET_LINE_REGEX.test(line);
};

const normalizeBulletLine = (line: string): string => {
  const match = line.match(BULLET_NORMALIZE_REGEX);
  if (!match) {
    return line;
  }
  const indent = match[1] ?? '';
  const marker = match[2];
  const nextChar = match[3];
  if (!marker || !nextChar) {
    return line;
  }
  if ((marker === '-' || marker === '*') && nextChar === marker) {
    return line;
  }
  const rest = line.slice(indent.length + marker.length).trimStart();
  if (rest.length === 0) {
    return line;
  }
  return `${indent}- ${rest}`;
};

const splitInlineBulletMarkers = (text: string): string => {
  const lines = text.split(/\r?\n/);
  const updated = lines.map((line) => {
    let nextLine = line;
    const symbolMatches = nextLine.match(INLINE_SYMBOL_BULLET_COUNT_REGEX);
    const symbolCount = symbolMatches?.length ?? 0;
    if (symbolCount > 0) {
      nextLine = nextLine.replace(
        INLINE_SYMBOL_BULLET_SPLIT_REGEX,
        (match, offset, full) => {
          const prefix = String(full).slice(0, Number(offset));
          const isLineStart = prefix.trim().length === 0;
          return isLineStart ? '- ' : '\n- ';
        }
      );
    }

    const matches = nextLine.match(INLINE_BULLET_COUNT_REGEX);
    const inlineCount = matches?.length ?? 0;
    const shouldSplit =
      inlineCount >= 2 ||
      (isBulletLine(nextLine) &&
        inlineCount >= 1 &&
        INLINE_BULLET_PUNCTUATION_REGEX.test(nextLine));
    if (!shouldSplit) {
      return nextLine;
    }
    return nextLine.replace(INLINE_BULLET_SPLIT_REGEX, '\n- ');
  });
  return updated.join('\n');
};

export const normalizeBulletMarkers = (text: string): string => {
  const lines = text.split(/\r?\n/);
  return lines.map((line) => normalizeBulletLine(line)).join('\n');
};

export const countBulletLines = (text: string): number => {
  return text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .filter((line) => isBulletLine(line)).length;
};

export const isOnlyBulletList = (text: string): boolean => {
  const nonEmptyLines = text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  if (nonEmptyLines.length === 0) {
    return false;
  }
  return nonEmptyLines.every((line) => isBulletLine(line));
};

export const normalizeBulletSpacing = (text: string): string => {
  const lines = splitInlineBulletMarkers(text).split(/\r?\n/);
  const spacedLines = lines.map((line) => normalizeBulletLine(line));

  const normalized: string[] = [];
  let lastNonEmptyLine: string | null = null;
  for (let i = 0; i < spacedLines.length; i += 1) {
    const line = spacedLines[i] ?? '';
    if (line.trim().length === 0) {
      let nextIndex = i + 1;
      while (
        nextIndex < spacedLines.length &&
        spacedLines[nextIndex]?.trim().length === 0
      ) {
        nextIndex += 1;
      }
      const nextLine = spacedLines[nextIndex];
      if (isBulletLine(lastNonEmptyLine) && isBulletLine(nextLine)) {
        continue;
      }
    } else {
      lastNonEmptyLine = line;
    }
    normalized.push(line);
  }

  return normalized.join('\n');
};
